import { Form, Formik } from 'formik';
import { useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';
import { dealsQueueFormValidation } from '@modules/accounting/validation';
import { ILenderListItem } from '@modules/admin/Tools/shared/LenderControlsHeader';
import { submitCheckRequest } from '@modules/ContactManager/components/Tabs/components/Deals/components/CheckRequest/request/CheckRequestApi';
import { getOptions } from '@shared/commonFunctions';
import { SERVICES } from '@shared/constants/apiConfig';
import { CancelButton, SaveButton } from '@shared/inputs/Buttons';
import { CommonButtonWrapper } from '@styles/CommonStyles';
import { Modal } from '@shared/components/modals/Modal';
import { IDealSummary } from '@modules/ContactManager/components/Tabs/components/Deals/interfaces';
import { AlertModal } from '@shared/components/modals/AlertModal';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { cancellationReasons, ICreateCheckRequest, IPayeeLender } from '../interfaces';
import { IProductCancellations, IDealForCheckRequest } from './ImportProductCancel';
import { UnIdentifiedDealsForm } from './UnIdentifiedDealsForm';

interface IUnIdentifiedDealsModalProps {
  deals?: IDealSummary[];
  selectedDeal: IProductCancellations;
  unIdentifiedDealModalPosition: number;
  onClose: () => void;
  refreshParent?: () => void;
}
export const UnIdentifiedDealsModal = ({
  deals,
  selectedDeal,
  unIdentifiedDealModalPosition,
  onClose,
  refreshParent,
}: IUnIdentifiedDealsModalProps) => {
  const { data: lendersList } = useSWR<ILenderListItem[]>(`/${SERVICES.LENDER}/lenders`);
  const { data: payeeData = [] } = useSWR<IPayeeLender[]>(`/${SERVICES.LENDER}/checkrequestpayee`);
  const {
    data: dealResponse,
    error,
    isValidating,
  } = useSWR(selectedDeal?.dealId && `/${SERVICES.DEAL}/deals/${selectedDeal?.dealId}`);
  const [isChecked, setChecked] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  const lenderOptions = useMemo(() => {
    const sortedOptions =
      lendersList
        ?.filter((l) => l.code !== '')
        ?.sort((a: ILenderListItem, b: ILenderListItem) => a.name.toUpperCase().localeCompare(b.name.toUpperCase())) ?? [];

    return getOptions(sortedOptions, 'code');
  }, [lendersList]);

  const handleSubmit = useCallback(
    async (data: IDealForCheckRequest) => {
      if (dealResponse?.isBuyBack) {
        setShowAlert(true);
        return;
      }

      function matchPayeeName(paydata: IPayeeLender[], data: IDealForCheckRequest) {
        for (const p of paydata) {
          // Direct payeeName match
          if (p?.payeeName === data?.payeeName) {
            return data?.payeeName;
          }

          // GAP loan paid off with payeeName match
          if (p?.isPayOffAddressForGAP && p?.gapLoanPaidOffRequestInformation?.payeeName === data?.payeeName) {
            return data?.payeeName;
          }
        }

        return '';
      }

      const payload: ICreateCheckRequest = {
        productCancellationId: data.productCancellationCheckId,
        dealId: data?.dealId ?? null,
        requestedBy: '',
        assetTag: data?.assetTag ?? '',
        customerName: data?.customerName ?? '',
        relatedDeal: data?.relatedDeal ?? null,
        loanConsultant: data?.loanConsultant ?? '',
        checkRequestType: 2,
        reason: cancellationReasons.find((d) => d.value.toLowerCase() == data?.cancelReason.toLowerCase())?.label ?? '',
        requestComment: data?.comment ?? '',
        sellerAmountRefund: String(data.refundFromSeller) ?? '',
        customerAmountRefund: String(data.customerRefund) ?? '',
        productType: data?.product ?? '',
        payeeName: isChecked
          ? `${data?.payeeName ?? ''} ${data.address ?? ''}`
          : `${matchPayeeName(payeeData, data)} ${data?.address ?? ''}`,
        provider: 'NAC',
        lenderCode: data?.lenderName ?? '',
        cancelledDate: data?.cancelDate ?? '',
        achRefNumber: data?.referenceNumber ?? '',
        createdBy: '',
        amountRequested: data.amountRequested,
        includeInBatch: false,
      };
      await submitCheckRequest(payload);
      refreshParent && refreshParent();

      onClose();
    },
    [isChecked, payeeData, dealResponse?.isBuyBack]
  );

  return (
    <>
      <Modal
        open
        hideBackdrop
        resetPosition={unIdentifiedDealModalPosition}
        title="Check Request"
        dialogWidth="1000px"
        onClose={onClose}
      >
        {isValidating && <LoaderBackdrop open={(!dealResponse && !error) || isValidating} />}

        <Formik
          enableReinitialize
          validationSchema={dealsQueueFormValidation}
          initialValues={
            {
              ...selectedDeal,
              payeeName: selectedDeal?.payeeName ?? '',
              amountRequested: selectedDeal?.customerRefund ?? '',
              relatedDeal: deals?.find((d) => d.dealId == selectedDeal?.dealId)?.dealNumericId ?? '',
              cancelReason:
                cancellationReasons.find((d) => d.label.toLowerCase() == selectedDeal.cancelReason.toLowerCase())?.value ?? '',
              lenderName: selectedDeal.lenderCode ?? '',
            } as IDealForCheckRequest
          }
          onSubmit={handleSubmit}
        >
          {(props) => (
            <Form onSubmit={props.handleSubmit}>
              <UnIdentifiedDealsForm
                lenderOptions={lenderOptions}
                lendersError={!!error}
                payeeData={payeeData}
                isChecked={isChecked}
                setChecked={setChecked}
              />
              <CommonButtonWrapper direction="row">
                <SaveButton label="Create" />

                <CancelButton
                  label="Cancel"
                  onClick={onClose}
                />
              </CommonButtonWrapper>
            </Form>
          )}
        </Formik>
      </Modal>

      <AlertModal
        hideCancel
        open={showAlert}
        title="Alert"
        subtitle="Buyback Deal. Cannot Process Product Cancel Check"
        buttonText="Ok"
        onClose={() => setShowAlert(false)}
        onConfirm={() => setShowAlert(false)}
      />
    </>
  );
};
